import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { formatTokenCount } from "components/pages/Admin/CustomReport/formatters";

const THINKING_LEVELS = [
  ["", "Default"],
  ["minimal", "Minimal"],
  ["low", "Low"],
  ["medium", "Medium"],
  ["high", "High"],
];

/**
 * Collapsible admin panel for LLM provider/model/temperature/thinking/prompts.
 * Controlled: `onChange` receives a patch of camelCase fields. A provider
 * change also carries that provider's first model.
 */
export const LlmTuningPanel = ({
  idPrefix,
  title = "Admin: LLM tuning",
  defaultOpen = false,
  provider,
  modelName,
  modelOptions,
  modelsLoading,
  temperature,
  thinkingLevel,
  systemPrompt,
  userPrompt,
  onChange,
  systemPromptHint = "(leave blank for Langfuse default)",
  userPromptHint = "(leave blank for default user prompt)",
  systemPromptPlaceholder = "You are a math instructional coach...",
  userPromptPlaceholder = "Focus on turn-taking and building on peers' ideas.",
  systemPromptRows = 6,
  onResetPrompts,
  resetDisabled,
  promptsReadOnly = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const id = (field) => `${idPrefix}-${field}`;
  const models = modelOptions?.[provider] || [];
  const modelListed = models.some((m) => m.value === modelName);

  return (
    <TuningPanel>
      <TuningHeader type="button" aria-expanded={open} aria-controls={id("body")}
        onClick={() => setOpen((v) => !v)}>
        <span>{title}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
        />
      </TuningHeader>
      {open && (
        <TuningBody id={id("body")}>
          <TuningField>
            <FilterLabel htmlFor={id("provider")}>Provider</FilterLabel>
            <TuningSelect
              id={id("provider")}
              value={provider || ""}
              onChange={(e) => onChange({
                provider: e.target.value,
                modelName: modelOptions?.[e.target.value]?.[0]?.value ?? modelName,
              })}
              disabled={modelsLoading}
            >
              {modelOptions && Object.keys(modelOptions).map((p) => <option key={p} value={p}>{p}</option>)}
            </TuningSelect>
          </TuningField>
          <TuningField>
            <FilterLabel htmlFor={id("model")}>Model</FilterLabel>
            <TuningSelect
              id={id("model")}
              value={modelName || ""}
              onChange={(e) => onChange({ modelName: e.target.value })}
              disabled={modelsLoading || !modelOptions?.[provider]}
            >
              {modelName && !modelListed && <option value={modelName}>{modelName}</option>}
              {models.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </TuningSelect>
          </TuningField>
          <TuningField>
            <FilterLabel htmlFor={id("temperature")}>Temperature: {temperature.toFixed(2)}</FilterLabel>
            <TuningRange
              id={id("temperature")}
              type="range"
              min="0"
              max="2"
              step="0.05"
              value={temperature}
              onChange={(e) => onChange({ temperature: parseFloat(e.target.value) })}
            />
          </TuningField>
          <TuningField>
            <FilterLabel htmlFor={id("thinking")}>
              Thinking Level <TuningHint>(Gemini 3.x only)</TuningHint>
            </FilterLabel>
            <TuningSelect
              id={id("thinking")}
              value={thinkingLevel}
              onChange={(e) => onChange({ thinkingLevel: e.target.value })}
            >
              {THINKING_LEVELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </TuningSelect>
          </TuningField>
          <TuningFieldFull>
            <FilterLabel htmlFor={id("system-prompt")}>
              System Prompt <TuningHint>{systemPromptHint}</TuningHint>
            </FilterLabel>
            <TuningTextarea
              id={id("system-prompt")}
              rows={systemPromptRows}
              placeholder={systemPromptPlaceholder}
              value={systemPrompt}
              readOnly={promptsReadOnly}
              onChange={(e) => onChange({ systemPrompt: e.target.value })}
            />
          </TuningFieldFull>
          <TuningFieldFull>
            <FilterLabel htmlFor={id("user-prompt")}>
              Prompt <TuningHint>{userPromptHint}</TuningHint>
            </FilterLabel>
            <TuningTextarea
              id={id("user-prompt")}
              rows={4}
              placeholder={userPromptPlaceholder}
              value={userPrompt}
              readOnly={promptsReadOnly}
              onChange={(e) => onChange({ userPrompt: e.target.value })}
            />
          </TuningFieldFull>
          {children}
          <TuningActions>
            <TuningSecondaryBtn type="button" disabled={resetDisabled} onClick={onResetPrompts}>
              Reset prompts
            </TuningSecondaryBtn>
          </TuningActions>
        </TuningBody>
      )}
    </TuningPanel>
  );
};

export const TokenSummaryBox = ({ rows }) => {
  const total = rows.reduce((sum, row) => sum + row.tokens, 0);
  return (
    <TokenSummary>
      {rows.map((row) => (
        <TokenRow key={row.label}>
          <span>{row.label}</span>
          <TokenValue>{formatTokenCount(row.tokens)} tokens</TokenValue>
        </TokenRow>
      ))}
      <TokenTotalRow>
        <TotalLabel>Estimated total</TotalLabel>
        <TotalLabel>~{formatTokenCount(total)} tokens</TotalLabel>
      </TokenTotalRow>
      <TokenDisclaimer>Token counts are approximate and may vary by model and provider.</TokenDisclaimer>
    </TokenSummary>
  );
};

export const FilterBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 0 16px;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 300px;
`;

export const FilterLabel = styled.label`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: black;
`;

export const TuningPanel = styled.div`
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  margin: 0 16px;
`;

const TuningHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
`;

const TuningBody = styled.div`
  display: flex;
  gap: 16px;
  padding: 0 16px 16px;
  flex-wrap: wrap;
`;

const TuningField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 200px;
  flex: 1;
`;

const TuningSelect = styled.select`
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  padding: 8px 12px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  background: white;
`;

const TuningRange = styled.input`
  width: 100%;
`;

export const TuningFieldFull = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 100%;
  min-width: 100%;
`;

export const TuningHint = styled.span`
  color: ${colors.milkGlass950};
  font-weight: 400;
  font-size: 12px;
`;

export const TuningTextarea = styled.textarea`
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  padding: 8px 12px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: ${colors.nightlyWoods500};
  background: white;
  resize: vertical;
`;

const TuningActions = styled.div`
  display: flex;
  gap: 8px;
  flex: 1 1 100%;
  justify-content: flex-end;
`;

export const TuningSecondaryBtn = styled.button`
  background: white;
  color: ${colors.blue500};
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  padding: 8px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DebugToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};

  label {
    cursor: pointer;
    margin: 0;
  }

  strong {
    font-weight: 600;
  }
`;

export const GenerateBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 16px;
`;

export const GenerateButton = styled(Button)`
  background-color: ${colors.nightlyWoods500};
  border: none;
  border-radius: 6px;
  font-size: 16px;
  height: 40px;
  padding: 0 16px;
  width: 100%;

  &:hover,
  &:focus,
  &:active {
    background-color: ${colors.nightlyWoods700};
  }
`;

const TokenSummary = styled.div`
  padding: 12px 16px;
  background-color: ${colors.milkGlass200};
  border-radius: 8px;
  font-size: 13px;
  color: #6c757d;
`;

const TokenRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const TokenValue = styled.span`
  color: ${colors.nightlyWoods900};
  font-weight: 500;
`;

const TokenTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${colors.milkGlass600};
  padding-top: 6px;
  margin-top: 2px;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: ${colors.nightlyWoods900};
`;

const TokenDisclaimer = styled.div`
  margin-top: 6px;
  font-size: 11px;
  font-style: italic;
`;

export const StaleNote = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${colors.orange700};
`;
